// Lê a variante de tema ativa (--mexx-* / --status-*, carregadas pelo components/theme/ThemeLoader.jsx)
// para que gráficos recharts acompanhem a cor do whitelabel em vez de usar hex fixo.
const FALLBACK = {
  primary: '#8BC34A',
  primaryDark: '#689F38',
  primaryLight: '#C8E6C9',
  primaryHover: '#9CCC65',
  success: '#8BC34A',
  warning: '#FF9800',
  error: '#F44336',
  info: '#2196F3',
  neutral: '#9E9E9E',
  textPrimary: '#212121',
  textSecondary: '#757575',
  gridLine: '#E0E0E0',
};

const readVar = (name, fallback) => {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value && value.trim() !== '' ? value.trim() : fallback;
};

export const getChartColors = () => {
  const theme = {
    primary: readVar('--mexx-green-primary', FALLBACK.primary),
    primaryDark: readVar('--mexx-green-dark', FALLBACK.primaryDark),
    primaryLight: readVar('--mexx-green-light', FALLBACK.primaryLight),
    primaryHover: readVar('--mexx-green-hover', FALLBACK.primaryHover),
    success: readVar('--status-success', FALLBACK.success),
    warning: readVar('--status-warning', FALLBACK.warning),
    error: readVar('--status-error', FALLBACK.error),
    info: readVar('--status-info', FALLBACK.info),
    neutral: readVar('--status-neutral', FALLBACK.neutral),
    // Roxo puramente decorativo (sem token de tema equivalente ainda) — só pra dar mais
    // variedade a paletas categóricas com muitos itens (ex.: 5 KPIs), igual outras cores
    // "categorical" que também não carregam significado de marca.
    accentPurple: readVar('--mexx-accent-purple', '#8E6FCE'),
    textPrimary: readVar('--mexx-gray-800', FALLBACK.textPrimary),
    textSecondary: readVar('--mexx-gray-600', FALLBACK.textSecondary),
    gridLine: readVar('--mexx-gray-300', FALLBACK.gridLine),
  };

  theme.categorical = [
    theme.primary,
    theme.info,
    theme.warning,
    theme.error,
    theme.primaryDark,
    theme.neutral,
  ];

  return theme;
};

// Clareia uma cor hex pra usar como fundo "pastel" de selo/badge (estilo emprestado do novo
// padrão visual de dashboards) sem precisar de um token de tema novo pra cada cor de destaque.
export const withAlpha = (hex, alpha) => {
  if (typeof hex !== 'string' || hex.charAt(0) !== '#') return hex;
  const clean = hex.slice(1);
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const int = parseInt(full, 16);
  if (Number.isNaN(int)) return hex;
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Escolhe a cor de texto legível sobre um fundo dinâmico, pela luminância relativa do WCAG.
// Existe porque os selos de score (CSAT/NPS) pintam o fundo conforme a nota: num fundo claro
// (amarelo, âmbar, verde) o texto escuro tem muito mais contraste, e num fundo escuro
// (vermelho de alerta) é o branco que ganha. Fixar uma cor só deixaria um dos casos ruim.
export const readableTextColor = hex => {
  if (typeof hex !== 'string' || hex.charAt(0) !== '#') return '#111111';
  const clean = hex.slice(1);
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const int = parseInt(full, 16);
  if (Number.isNaN(int)) return '#111111';

  const channel = value => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel((int >> 16) & 255)
    + 0.7152 * channel((int >> 8) & 255)
    + 0.0722 * channel(int & 255);

  const contrastWithDark = (luminance + 0.05) / (0.05 + 0.0176);
  const contrastWithLight = 1.05 / (luminance + 0.05);

  return contrastWithLight > contrastWithDark ? '#ffffff' : '#111111';
};

// Paleta categorica do donut de canais, em ORDEM FIXA (nunca ciclada).
//
// A de getChartColors().categorical reaproveita os tokens do tema, e ali primary e primaryDark
// sao dois verdes vizinhos: com 5+ canais o grafico repetia/aproximava cor e ficava impossivel
// dizer qual fatia era qual -- foi o que o teste apontou. Cor categorica nao e cor de marca:
// identifica uma entidade, entao aqui ela e fixa e nao acompanha o whitelabel.
//
// Os seis passos foram escolhidos por separacao perceptual e conferidos com o validador de
// paleta (deuteranopia/protanopia/tritanopia sobre fundo branco, TODOS os pares, nao so os
// vizinhos): pior par com dE 9.9, acima do alvo de 8. Dois deles ficam abaixo de 3:1 de
// contraste com o branco, o que exige rotulo visivel -- a legenda ao lado do donut nomeia cada
// fatia com valor e percentual, entao a identidade nunca depende so da cor.
//
// Mexer em um valor sozinho quebra a garantia: revalide o conjunto inteiro antes.
export const CHANNEL_PALETTE = ['#3737BE', '#BE7A37', '#87C34B', '#9464C4', '#37A7BE', '#A51D4A'];

// Cor de quem nao coube na paleta (fatia "Outros"): cinza neutro, de proposito -- e um balde,
// nao um canal.
export const CHANNEL_OTHER_COLOR = '#8A94A6';

export default getChartColors;
